// =====================================================================
// My Agency V2 — badge pills (ARTBOARD-SPEC §4 CMP-PILL, exact values).
//
// Pill order rule on cards (board 1f, binding): Admin → Lawyer → license
// signal. License rule (board 2g, binding): "Licensed" is system-derived;
// ABSENCE RENDERS NOTHING — never the word "Unlicensed" on cards or
// public surfaces (the dot line in glyphs.js is for internal admin
// contexts only).
//
// All labels are props — sample values in the artboard are ILLUSTRATIVE.
// =====================================================================
import React from "react";
import { LinkIcon, ClockIcon, WarningTriangleIcon, CalendarIcon } from "./icons";

// Base badge: 11px/600, padding 2.5px 8px, radius 999.
export const Pill = ({ bg, color, gap, style, children, ...rest }) => (
  <span
    style={{
      flex: "none",
      fontSize: 11,
      fontWeight: 600,
      padding: "2.5px 8px",
      borderRadius: 999,
      background: bg,
      color,
      ...(gap != null
        ? { display: "inline-flex", alignItems: "center", gap }
        : null),
      ...style,
    }}
    {...rest}
  >
    {children}
  </span>
);

export const AdminPill = ({ children, ...rest }) => (
  <Pill bg="#EAF3FF" color="#1668D9" {...rest}>
    {children}
  </Pill>
);

export const LawyerPill = ({ children, ...rest }) => (
  <Pill bg="#F3EFFF" color="#6D4FC2" {...rest}>
    {children}
  </Pill>
);

// The green "Licensed" pill (board 2g) was REPLACED by the FIFA Verified
// badge (design D-A): components/common/FifaVerifiedBadge.js, gated by
// isFifaVerified (Marin 2026-08-04: self-declaration carries the badge;
// only failed_qr hides it). No separate credential chip exists anymore.

// Linked partner pill (4a): brand-tinted, leading chain.
export const LinkedPill = ({ children, ...rest }) => (
  <Pill bg="#EAF3FF" color="#1668D9" gap={4} {...rest}>
    <LinkIcon size={10} />
    {children}
  </Pill>
);

// Pending partner pill (4a): amber, leading clock.
export const PendingPill = ({ children, ...rest }) => (
  <Pill bg="#FEF3C7" color="#92400E" gap={4} {...rest}>
    <ClockIcon size={10} strokeWidth={2.4} stroke="#92400E" />
    {children}
  </Pill>
);

// Count pill (CMP-TABS / pending-invitations header).
// variant: "active" (brand) | "inactive" (grey) | "amber" (pending count).
// mobile: 11px, padding 1px 6px (desktop 11.5px, 1.5px 7px).
const COUNT_STYLES = {
  active: { background: "#EAF3FF", color: "#1668D9" },
  inactive: { background: "#EAEFF5", color: "#64748B" },
  amber: { background: "#FEF3C7", color: "#92400E" },
};
export const CountPill = ({ variant = "inactive", mobile = false, style, children, ...rest }) => (
  <span
    style={{
      flex: "none",
      fontSize: mobile ? 11 : 11.5,
      fontWeight: 600,
      padding: mobile ? "1px 6px" : "1.5px 7px",
      borderRadius: 999,
      ...COUNT_STYLES[variant],
      ...style,
    }}
    {...rest}
  >
    {children}
  </span>
);

// Position sub-pill (players): 10.5px/600, grey (values ILLUSTRATIVE).
export const PositionSubPill = ({ style, children, ...rest }) => (
  <span
    style={{
      flex: "none",
      fontSize: 10.5,
      fontWeight: 600,
      padding: "2px 7px",
      borderRadius: 999,
      background: "#EAEFF5",
      color: "#475569",
      ...style,
    }}
    {...rest}
  >
    {children}
  </span>
);

// Contract status pill family (2a): 12px/600, padding 4px 10px, gap 6.
// status: "expired" | "expiring" | "dated" | "none".
// The LABEL comes from existing platform logic (backend contract_urgency
// first, client radar fallback — FUNCTION-INVENTORY §9) — never derive
// timing rules from the mock (Rule 10).
const CONTRACT_STYLES = {
  expired: { background: "#FEE2E2", color: "#B91C1C" },
  expiring: { background: "#FEF3C7", color: "#92400E" },
  dated: { background: "#EAEFF5", color: "#475569" },
};
const contractIcon = (status) => {
  if (status === "expired") return <WarningTriangleIcon size={11} strokeWidth={2.4} stroke="#B91C1C" />;
  if (status === "expiring") return <ClockIcon size={11} strokeWidth={2.4} stroke="#92400E" />;
  if (status === "dated") return <CalendarIcon size={11} strokeWidth={2.2} stroke="#475569" />;
  return null;
};
export const ContractStatusPill = ({ status = "dated", style, children, ...rest }) => {
  if (status === "none") {
    // No-data pill: white, dashed border, 500 weight, padding 3.5px 10px.
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 500,
          padding: "3.5px 10px",
          borderRadius: 999,
          background: "#fff",
          border: "1.5px dashed #C4CEDA",
          color: "#64748B",
          ...style,
        }}
        {...rest}
      >
        {children}
      </span>
    );
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        padding: "4px 10px",
        borderRadius: 999,
        ...CONTRACT_STYLES[status],
        ...style,
      }}
      {...rest}
    >
      {contractIcon(status)}
      {children}
    </span>
  );
};
